import React from "react"
import PropTypes from "prop-types"
import "stylesheets/ScorePage/TrendBadge.scss"

const SYMBOL_MAP = {
  up: ["up", "UP", "\u2191", "↑", "\u00e2\u2020\u2018"],
  down: ["down", "DOWN", "\u2193", "↓", "\u00e2\u2020\u2019"],
  equal: ["="],
  new: ["new"],
}

const normalizeSymbol = (symbol) => {
  if (!symbol) return null
  if (SYMBOL_MAP.up.includes(symbol)) return "up"
  if (SYMBOL_MAP.down.includes(symbol)) return "down"
  if (SYMBOL_MAP.equal.includes(symbol)) return "equal"
  if (SYMBOL_MAP.new.includes(symbol)) return "new"
  return null
}

const TrendBadge = ({
  trendBadge,
  size = "medium",
  variant = "trend",
  color = "auto",
}) => {
  const normalizedSymbol = normalizeSymbol(trendBadge?.symbol)
  if (!normalizedSymbol || trendBadge.value == null) return null

  const getNormalizedValue = (value, currentVariant) => {
    if (value === "=") return "="
    if (value === "new") return "New"

    const numericValue =
      typeof value === "string"
        ? Number.parseFloat(value.replace("%", ""))
        : value

    const rounded = Math.round(numericValue)
    if (rounded === 0) return "="
    if (currentVariant === "score") return rounded.toString()

    return `${rounded}%`
  }

  const normalizedValue = getNormalizedValue(trendBadge.value, variant)
  const effectiveSymbol = normalizedValue === "=" ? "equal" : normalizedSymbol

  const containerClasses = [
    "trend-badge",
    `trend-badge--${size}`,
    `trend-badge--${variant}`,
    `trend-badge--color-${color}`,
    `trend-badge--symbol-${effectiveSymbol}`,
    normalizedValue === "=" && "trend-badge--stable",
    effectiveSymbol === "new" && "trend-badge--new",
  ]
    .filter(Boolean)
    .join(" ")

  const renderArrowSvg = () => {
    const svgSize = size === "small" ? 12 : size === "large" ? 16 : 14

    if (effectiveSymbol === "up") {
      return (
        <svg
          width={svgSize}
          height={svgSize}
          viewBox="0 0 16 16"
          className="trend-badge__arrow-svg"
        >
          <path d="M8 1L15 12H1L8 1Z" fill="currentColor" />
        </svg>
      )
    }

    if (effectiveSymbol === "down") {
      return (
        <svg
          width={svgSize}
          height={svgSize}
          viewBox="0 0 16 16"
          className="trend-badge__arrow-svg"
        >
          <path d="M8 15L1 4H15L8 15Z" fill="currentColor" />
        </svg>
      )
    }

    return null
  }

  let content
  if (normalizedValue === "=") {
    content = <span className="trend-badge__text">=</span>
  } else if (effectiveSymbol === "new") {
    content = <span className="trend-badge__text">{normalizedValue}</span>
  } else {
    content = (
      <>
        <span className="trend-badge__arrow">{renderArrowSvg()}</span>
        <span className="trend-badge__text">{normalizedValue}</span>
      </>
    )
  }

  return (
    <div className={containerClasses}>
      <div className="trend-badge__content">{content}</div>
    </div>
  )
}

TrendBadge.propTypes = {
  trendBadge: PropTypes.shape({
    symbol: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  variant: PropTypes.oneOf(["trend", "score"]),
  color: PropTypes.oneOf(["auto", "auto-inverse", "white", "planet"]),
}

TrendBadge.defaultProps = {
  color: "auto",
}

export default TrendBadge
